import { HelpOutline } from '@mui/icons-material'
import { Box, Divider, Paper, Stack, Tooltip } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import LabeledTextField from '@/components/dataDisplay/LabeledTextField'
import Page from '@/components/layouts/Page'
import { usePermissions } from '@/features/authorization/contexts/PermissionsContext'
import { ControllerElectricianUILinks } from '@/features/controller/components/ControllerElectricianUILinks'
import { ControllerFirmwareDetails } from '@/features/controller/components/ControllerFirmwareDetails'
import { ControllerObservabilityDetails } from '@/features/controller/components/ControllerObservabilityDetails'
import { SingleControllerConfigTree } from '@/features/controller/configuration/components/tree/SingleControllerConfigTree'
import { ControllerConfigurationFileSetProvider } from '@/features/controller/configuration/contexts/ControllerConfigurationFileSetContext'
import { ControllerRouteInformation } from '@/features/controller/constants'
import { useControllerDescriptionQuery } from '@/features/controller/hooks/useControllerDescriptionQuery'
import { ControllerTabs } from '@/features/controller/pages/components/ControllerTabs'
import { ControllerRestartDetails } from '@/features/controller/restart/components/ControllerRestartDetails'
import { ControllerRestartProvider } from '@/features/controller/restart/contexts/ControllerRestartContext'
import { renderControllerName } from '@/features/controller/utils/controllerNames'
import { renderTimestamp } from '@/features/controller/utils/renderTimestamp'
import NavigateToError from '@/features/error/pages/NavigateToError'
import { useSiteQuery } from '@/features/site/hooks/useSiteQuery'

export function ControllerDetailsPage() {
  const { controllerId } = useParams()
  const { t, i18n } = useTranslation()
  const { permissions } = usePermissions()
  const canMutateControllers = permissions.has('controllersMutation')
  const { controllerDescription, isFetched, isLoading } = useControllerDescriptionQuery(controllerId!, {
    enabled: Boolean(controllerId),
  })
  const siteId = controllerDescription?.siteId
  const { site, isLoading: isLoadingSite } = useSiteQuery({ uuid: siteId }, { enabled: Boolean(siteId) })

  if (!controllerDescription && isFetched) {
    return <NavigateToError statusCode={404} />
  }

  const isControllerFound = controllerId && controllerDescription

  return (
    <Page
      isLoading={isLoading}
      pageHeaderProps={{
        pageTitle: `${t('controllers.title')}`,
        isRoot: true,
        appBarContent: (
          <CustomTypography>
            {t('controllers.controller')}{' '}
            {controllerDescription ? renderControllerName(controllerDescription) : controllerId!}
          </CustomTypography>
        ),
      }}
    >
      {isControllerFound && (
        <ControllerRestartProvider>
          <ControllerTabs
            activeTab={ControllerRouteInformation.CONTROLLER_DETAILS.routePath}
            controllerId={controllerId}
          />
          <Box my={2}>
            <Stack direction="row" gap={2}>
              <Stack spacing={2} sx={{ flex: 2 }}>
                <Paper sx={{ p: 3, height: 'fit-content', width: '100%' }}>
                  <Stack direction="column" gap={2}>
                    <Stack alignItems="center" direction="row" justifyContent="space-between">
                      <LabeledTextField
                        label={t('controllers.details.equipment_number')}
                        text={controllerDescription.equipmentNumber?.toString() || '-'}
                      />
                      <LabeledTextField
                        label={t('controllers.details.created_at')}
                        text={renderTimestamp(controllerDescription.createdAt, i18n.language)}
                      />
                    </Stack>
                    {isLoadingSite ? (
                      <CircularProgress title="Loading site" />
                    ) : (
                      site && (
                        <Stack flexDirection="row">
                          <Box flex={1}>
                            <Stack alignItems="center" direction="row" gap={1}>
                              <CustomTypography color="grey.500" variant="caption">
                                {t('controllers.details.site')}
                              </CustomTypography>
                              <Tooltip title={t('controllers.details.site_tooltip')}>
                                <HelpOutline fontSize="small" sx={{ color: 'grey.500' }} />
                              </Tooltip>
                            </Stack>
                            <CustomTypography>{site?.name}</CustomTypography>
                          </Box>
                          <ControllerElectricianUILinks controller={controllerDescription} flex={1} site={site} />
                        </Stack>
                      )
                    )}
                  </Stack>
                </Paper>
                <Paper sx={{ p: 3 }}>
                  <ControllerObservabilityDetails controller={controllerDescription} site={site || undefined} />
                </Paper>
              </Stack>
              <Paper sx={{ p: 3, flex: 2, height: 'fit-content' }}>
                <Stack divider={<Divider flexItem />} spacing={2}>
                  {canMutateControllers && <ControllerRestartDetails controllerId={controllerId} />}
                  <ControllerFirmwareDetails controller={controllerDescription} />
                </Stack>
              </Paper>
            </Stack>
            {canMutateControllers && (
              <ControllerConfigurationFileSetProvider controllerId={controllerId}>
                <SingleControllerConfigTree controller={controllerDescription} site={site} />
              </ControllerConfigurationFileSetProvider>
            )}
          </Box>
        </ControllerRestartProvider>
      )}
    </Page>
  )
}
